/**
 * Agent-tool-execution dashboard — application shell.
 *
 * Composes the panel registry (`./app/panels-registry.tsx`), the
 * overview landing page (`./app/OverviewPanel.tsx`), the hash-routing
 * tab state, the inline summary panels (bridges / matrix / graph /
 * footprint / ledger), and the keyboard navigator.
 *
 * Since issue #2128 the dashboard data is bundled at build time and
 * validated once at module init, so the shell no longer carries
 * loading-state UI.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import "../../styles/tailwind.css";

import React, {
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DemoPanelGroupNav, SkipLink, useUrlHashRoute } from "@csk/demo-ui";
import { isEditableElementTarget } from "@csk/demo-ui/utils";
import { PathSelectionContext, type PathSelection } from "./context";
import { useData } from "./hooks/useData";
import { METRICS, ALL_TABS, TAB_GROUPS } from "./constants";
import type { Bridge } from "./types";
import {
  PanelFallback,
  buildPanelProps,
  isLazyPanelKey,
  panels,
} from "./app/panels-registry";
import { OverviewPanel } from "./app/OverviewPanel";
import {
  BridgesPanel,
  FootprintPanel,
  GraphPanel,
  LedgerPanel,
  MatrixPanel,
} from "./app/InlinePanels";

function buildTabIndex(): {
  readonly ids: ReadonlySet<string>;
  readonly labelsById: ReadonlyMap<string, string>;
} {
  const ids = new Set<string>();
  const labelsById = new Map<string, string>();
  for (const tab of ALL_TABS) {
    ids.add(tab.id);
    labelsById.set(tab.id, tab.label);
  }
  return { ids, labelsById };
}

const TAB_INDEX = buildTabIndex();
const TAB_IDS = TAB_INDEX.ids;

function isKnownTabId(id: string): id is string {
  return TAB_IDS.has(id);
}

export function App(): React.JSX.Element {
  const data = useData();
  const [tab, setTab] = useUrlHashRoute<string>(
    "panel",
    "overview",
    isKnownTabId,
  );
  const [pathSelection, setPathSelection] = useState<PathSelection>(null);

  // Update document title to reflect the selected panel.
  useEffect(() => {
    const label = TAB_INDEX.labelsById.get(tab) ?? "Dashboard";
    document.title = `CSK · ${label}`;
  }, [tab]);

  // Keyboard shortcut: Alt + 1..9 selects one of the first nine tabs.
  // Browser-reserved chords (Ctrl/⌘ + digit) cannot be reliably intercepted, so
  // Alt is used and edits-in-progress are excluded so digits keep their normal
  // meaning when the user is typing.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onKey = (e: KeyboardEvent): void => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (isEditableElementTarget(e.target)) return;
      const idx = parseInt(e.key, 10);
      if (Number.isInteger(idx) && idx >= 1 && idx <= ALL_TABS.length) {
        const targetTab = ALL_TABS[idx - 1];
        e.preventDefault();
        setTab(targetTab.id);
      }
    };
    window.addEventListener("keydown", onKey);
    return (): void => {
      window.removeEventListener("keydown", onKey);
    };
  }, [setTab]);

  const byGenerator = useMemo(() => {
    const m = new Map<string, Bridge[]>();
    if (!data.bridges) return m;
    for (const b of data.bridges) {
      const g = b.generator ?? "unknown";
      const existing = m.get(g);
      if (existing) existing.push(b);
      else m.set(g, [b]);
    }
    return m;
  }, [data.bridges]);

  const matrixSummary = useMemo(() => {
    if (!data.matrix) return { allowed: 0, forbidden: 0 };
    let allowed = 0;
    let forbidden = 0;
    for (const r of data.matrix.rules) {
      if (r.status === "allowed") allowed++;
      else forbidden++;
    }
    return { allowed, forbidden };
  }, [data.matrix]);

  const renderPanel = (): React.ReactNode => {
    switch (tab) {
      case "overview":
        return (
          <OverviewPanel
            matrixSummary={matrixSummary}
            byGenerator={byGenerator}
          />
        );
      case "bridges":
        return data.bridges ? <BridgesPanel byGenerator={byGenerator} /> : null;
      case "matrix":
        return data.matrix ? <MatrixPanel matrix={data.matrix} /> : null;
      case "graph":
        return data.matrix ? <GraphPanel matrix={data.matrix} /> : null;
      case "footprint":
        return data.footprint ? (
          <FootprintPanel footprint={data.footprint} />
        ) : null;
      case "ledger":
        return data.ledger ? <LedgerPanel ledger={data.ledger} /> : null;
      default:
        return renderLazyPanel(tab);
    }
  };

  const renderLazyPanel = (tabId: string): React.ReactNode => {
    if (!isLazyPanelKey(tabId)) {
      return (
        <div className="text-slate-500 text-sm">Unknown panel: {tabId}</div>
      );
    }
    const Panel = panels[tabId] as React.ComponentType<object>;
    const props = buildPanelProps(tabId, data);
    return (
      <Suspense fallback={<PanelFallback />}>
        <Panel {...props} />
      </Suspense>
    );
  };

  const activeLabel = TAB_INDEX.labelsById.get(tab) ?? tab;
  const mainRef = useRef<HTMLElement | null>(null);
  const errorCount = data.errors.length;

  return (
    <PathSelectionContext.Provider
      value={{
        selection: pathSelection,
        setSelection: (s) => {
          setPathSelection(s);
          setTab("path");
        },
      }}
    >
      <div
        data-csk-demo="agent-tool-execution"
        data-csk-demo-ready="true"
        className="min-h-screen text-slate-100 antialiased"
        style={{
          background: [
            "linear-gradient(135deg, rgba(56, 189, 248, 0.14) 0%, transparent 34%)",
            "linear-gradient(215deg, rgba(34, 197, 94, 0.1) 0%, transparent 42%)",
            "linear-gradient(160deg, #07101d 0%, #0d1624 100%)",
          ].join(", "),
        }}
      >
        <SkipLink
          targetId="csk-main"
          label="Skip to panel"
          style={{
            background: "#38bdf8",
            color: "#0a0e17",
            borderColor: "transparent",
            fontFamily: "inherit",
          }}
        />
        <div className="mx-auto max-w-7xl px-5 py-5">
          <header className="mb-4 flex items-baseline gap-3 border-b border-slate-700/30 pb-4 pt-3">
            <h1 className="text-lg font-bold text-slate-50 tracking-tight">
              CSK
            </h1>
            <span className="text-xs text-slate-400">
              Governance Dashboard · v0.9.9
            </span>
            <span
              className="ml-auto text-[10px] text-slate-400 font-mono"
              aria-live="polite"
            >
              {errorCount > 0
                ? `${errorCount} fixture ${errorCount === 1 ? "error" : "errors"}`
                : "ready"}
            </span>
          </header>

          <DemoPanelGroupNav
            groups={TAB_GROUPS.map((group) => ({
              id: group.label,
              label: group.label,
              color: group.color.accent,
              tabs: group.tabs,
            }))}
            activeId={tab}
            onSelect={setTab}
            tabpanelId="csk-main"
            ariaLabel="Dashboard panels"
          />

          {errorCount > 0 && (
            <div
              role="alert"
              className="mt-3 rounded border border-red-900/30 bg-red-950/20 p-2 text-red-400 text-xs"
            >
              {data.errors.map((e, i) => (
                <div key={i}>{e}</div>
              ))}
            </div>
          )}

          <main
            ref={mainRef}
            id="csk-main"
            role="tabpanel"
            aria-labelledby={`tab-${tab}`}
            aria-label={`${activeLabel} panel`}
            tabIndex={-1}
            className="mt-5"
          >
            {renderPanel()}
          </main>

          <footer className="mt-10 border-t border-slate-800/40 pt-4 text-[10px] text-slate-600 font-mono flex flex-wrap gap-x-3 gap-y-1">
            <span>
              Press{" "}
              <kbd className="rounded border border-slate-800 bg-slate-900/60 px-1">
                Alt
              </kbd>
              +
              <kbd className="rounded border border-slate-800 bg-slate-900/60 px-1">
                1
              </kbd>
              …
              <kbd className="rounded border border-slate-800 bg-slate-900/60 px-1">
                9
              </kbd>{" "}
              to jump panels.
            </span>
            <span>
              {ALL_TABS.length} panels · {METRICS.theorems} theorems ·{" "}
              {METRICS.testsPassed} tests
            </span>
          </footer>
        </div>
      </div>
    </PathSelectionContext.Provider>
  );
}
